import { ROLES, EDIT_ROLES, CUSTOMER_EDIT_ROLES, TAKIP_ROLLERI, ADMIN_ONLY, ALL_ROLES, NON_VIEW_ROLES, ACCOUNTING_ROLES } from '../shared/constants/roles';

/**
 * sayfaRolleri.ts — hangi rol hangi sayfayı açabilir, TEK yerde (rota 3.39).
 *
 * Bu bilgi eskiden iki yerde elle yazılıyordu: `App.tsx`in rota kapıları ve kenar
 * çubuğunun menüsü. İkisi sessizce ayrıştı. Pazarlama rotalarda operasyonla aynı
 * sayfaları açabiliyordu, menüde yalnız "Müşteriler"i görüyordu (3.39); muhasebe
 * menüde rapor ikonunu görüp tıklayınca açamadığı sayfadan panoya atılıyordu
 * (3.37). Sayfa içindeki bağlantılar da aynı bilgiyi hiç sormuyordu: teknik ürün
 * adına basınca, görüntüleme üretim ekranı logosuna basınca panoya düşüyordu.
 *
 * Artık üç tüketici aynı tabloyu okur:
 *   1. `App.tsx` — rota kapısı (`allowedRoles`)
 *   2. `Sidebar` / `raporMenusu` — menü yalnız açılabilen sayfayı gösterir
 *   3. Sayfa içi bağlantılar — `sayfayiAcabilir` / `yolAcilabilir`
 *
 * Anahtar, `App.tsx`teki `path` metninin KENDİSİ. `sayfaRolleri.test.ts` rota
 * dosyasını tarar: her korumalı rota bu tablodan okumak, her satırın bir rotası
 * olmak zorunda.
 *
 * Arka uçta karşılığı olan satırlar (raporlar, müşteri yazma) sunucunun rol
 * listesiyle AYNI olmalı; o eşlik `backend/tests/pazarlamaRolKapisi.test.js`te.
 */

// Anahtarları harf harf korur (yanlış yazılmış yol derlemede kırılır), değerleri
// yine de liste olarak denetler.
const tablo = <T extends Record<string, readonly string[]>>(satirlar: T): T => satirlar;

export const SAYFA_ROLLERI = tablo({
  // Rota kapısı YOK — giriş yapmış herkes. Tabloda menü okuyabilsin diye duruyor;
  // `App.tsx` bu iki rotaya liste vermez: listede olmayan bir rol `/` yönlendirmesinde
  // kendi kendine dönen bir döngüye girerdi.
  '/': ALL_ROLES,
  '/me/login-history': ALL_ROLES,

  '/production': ALL_ROLES,

  '/users': ADMIN_ONLY,
  '/users/new': ADMIN_ONLY,
  '/users/edit/:id': ADMIN_ONLY,
  '/users/:id/activity': ADMIN_ONLY,
  '/kilavuz': [ROLES.ADMIN, ROLES.OPERASYON],
  '/settings/integrations': ADMIN_ONLY,

  '/products': [...NON_VIEW_ROLES, ROLES.TEKNIK],
  '/products/images': EDIT_ROLES,
  '/products/catalog': ADMIN_ONLY,
  '/products/new': EDIT_ROLES,
  '/products/edit/:id': NON_VIEW_ROLES,
  '/materials': [ROLES.ADMIN, ROLES.TEKNIK],

  '/orders': [...NON_VIEW_ROLES, ROLES.TEKNIK],
  '/orders/new': EDIT_ROLES,
  '/orders/:id': [...NON_VIEW_ROLES, ROLES.TEKNIK],

  '/customers': NON_VIEW_ROLES,
  '/customers/new': CUSTOMER_EDIT_ROLES,
  '/customers/follow-ups': TAKIP_ROLLERI,
  '/customers/match-suggestions': EDIT_ROLES,
  '/customers/groups': ADMIN_ONLY,
  '/customers/:id': NON_VIEW_ROLES,

  // Toplantıları her rol görür; yazma düğmeleri sayfanın içinde rolle açılır.
  '/toplantilar': ALL_ROLES,
  '/toplantilar/gorevler': ALL_ROLES,
  '/toplantilar/:id': ALL_ROLES,

  // Rapor merkezi ve kategori sayfaları muhasebeye ve pazarlamaya kapalı (3.37
  // kararı, 3.39'da korundu): ikisine kenar çubuğunda doğrudan bağlantı verilir.
  '/reports': [...EDIT_ROLES, ROLES.TEKNIK],
  '/reports/:categoryId': [...EDIT_ROLES, ROLES.TEKNIK],
  '/reports/operational/teslimat-ciro': [...EDIT_ROLES, ROLES.TEKNIK],
  '/reports/teslim-tutanagi': [ROLES.ADMIN, ROLES.OPERASYON, ROLES.TEKNIK],
  '/reports/operational/termin-teslim-performansi': EDIT_ROLES,
  '/reports/financial/financial-revenue-breakdown': [...EDIT_ROLES, ROLES.TEKNIK, ROLES.MUHASEBE],
  '/reports/product-analysis/product-performance': [...EDIT_ROLES, ROLES.PAZARLAMA],
  '/reports/sales-analysis/il-bazli-isi-haritasi': [ROLES.ADMIN, ROLES.OPERASYON, ROLES.PAZARLAMA],
  '/reports/payment-tracking': Array.from(new Set([...EDIT_ROLES, ...ACCOUNTING_ROLES])),
  '/reports/:categoryId/:moduleId': EDIT_ROLES,
});

export type SayfaYolu = keyof typeof SAYFA_ROLLERI;

/** Rol, tablodaki bu sayfayı açabilir mi. Yol desen olarak verilir (`/orders/:id`). */
export const sayfayiAcabilir = (yol: SayfaYolu, rol: string): boolean =>
  (SAYFA_ROLLERI[yol] as readonly string[]).includes(rol);

const parcala = (yol: string): string[] => yol.split(/[?#]/)[0].split('/').filter(Boolean);

/**
 * Gerçek bir adresi (`/orders/42`) tablodaki desene (`/orders/:id`) eşler.
 *
 * React Router gibi SABİT parçası çok olan desen kazanır: `/orders/new` kendi
 * satırına düşer, `/orders/:id`e değil; `/reports/sales-analysis/il-bazli-isi-haritasi`
 * de `/reports/:categoryId/:moduleId`e değil. Eşleşme yoksa `null` — `App.tsx`te
 * bilinmeyen adres panoya döner.
 */
export const yolunDeseni = (yol: string): SayfaYolu | null => {
  const parcalar = parcala(yol);
  let enIyi: SayfaYolu | null = null;
  let enIyiSabit = -1;
  for (const desen of Object.keys(SAYFA_ROLLERI) as SayfaYolu[]) {
    const desenParcalari = parcala(desen);
    if (desenParcalari.length !== parcalar.length) continue;
    let sabit = 0;
    const uyuyor = desenParcalari.every((parca, i) => {
      if (parca.startsWith(':')) return true;
      if (parca !== parcalar[i]) return false;
      sabit += 1;
      return true;
    });
    if (uyuyor && sabit > enIyiSabit) {
      enIyi = desen;
      enIyiSabit = sabit;
    }
  }
  return enIyi;
};

/** Rol bu gerçek adresi açabilir mi — sayfa içi bağlantıların kapısı. */
export const yolAcilabilir = (yol: string, rol: string): boolean => {
  const desen = yolunDeseni(yol);
  return desen !== null && sayfayiAcabilir(desen, rol);
};

/**
 * Üretim ekranının ürün tablosunda ürün adını ürün kartına bağlantı olarak gören roller.
 *
 * Ürün kartını açabilen her rol değil, bağlantıyı kullanan roller. Görüntülemeye
 * açmak için buraya eklemek yetmez: rol `/products/edit/:id` satırında da olmalı,
 * yoksa fonksiyon kapalı döner. O satırı açmadan önce fiyat kararı gerekir: kart
 * satış fiyatını ve fiyat geçmişini gösteriyor, kanal fiyatları ucu görüntülemeye kapalı.
 */
export const URETIM_URUN_KARTI_BAGLANTISI_ROLLERI: readonly string[] = [ROLES.ADMIN, ROLES.OPERASYON];

/**
 * Rol üretim ekranında ürün adını ürün kartına bağlantı olarak görür mü. Kartı
 * açıp açamayacağını söylemez: muhasebe kartı açar ama burada `false` alır.
 */
export const uretimdeUrunBaglantisiGorunur = (rol: string): boolean =>
  URETIM_URUN_KARTI_BAGLANTISI_ROLLERI.includes(rol) && sayfayiAcabilir('/products/edit/:id', rol);
